import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { ArrowRight, ArrowLeft, Calendar, Loader2, Clock } from 'lucide-react';
import TextMarquee from '../components/TextMarquee';
import { usePageImages } from '../hooks/usePageImages';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const NewBlog = () => {
  const { language } = useLanguage();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPost, setSelectedPost] = useState(null);
  const { getImage } = usePageImages('blog');

  useEffect(() => {
    fetchPosts();
  }, []);

  const fetchPosts = async () => {
    try {
      const response = await fetch(`${API_URL}/api/blog/posts`);
      if (response.ok) {
        const data = await response.json();
        setPosts(data);
      }
    } catch (error) {
      console.error('Error fetching posts:', error);
    } finally {
      setLoading(false);
    }
  };

  const content = {
    en: {
      overline: "The Journal",
      title: "Insights & Ideas",
      subtitle: "Thoughts on experience design, brand building, and running creative ventures.",
      readMore: "Read Article",
      loading: "Loading...",
      noPosts: "No posts yet. Check back soon!",
      backToBlog: "Back to Journal",
      readTime: "min read",
      marqueeText: ["Insights.", "Ideas.", "Stories.", "Lessons."]
    },
    es: {
      overline: "El Diario",
      title: "Perspectivas e Ideas",
      subtitle: "Reflexiones sobre diseño de experiencias, construcción de marca y emprendimientos creativos.",
      readMore: "Leer Artículo",
      loading: "Cargando...",
      noPosts: "Aún no hay publicaciones. ¡Vuelve pronto!",
      backToBlog: "Volver al Diario",
      readTime: "min de lectura",
      marqueeText: ["Perspectivas.", "Ideas.", "Historias.", "Lecciones."]
    }
  };

  const t = content[language];

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString(language === 'en' ? 'en-US' : 'es-ES', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-vainilla flex items-center justify-center">
        <Loader2 className="w-10 h-10 animate-spin text-terracota" />
      </div>
    );
  }

  // Show single post view
  if (selectedPost) {
    return (
      <div className="min-h-screen bg-vainilla" data-testid="blog-post-view">
        <article className="pt-32 pb-24 px-6">
          <div className="max-w-3xl mx-auto">
            <button
              onClick={() => setSelectedPost(null)}
              className="inline-flex items-center gap-2 text-terracota hover:text-stone-900 mb-10 font-semibold transition-colors"
              data-testid="back-to-blog"
            >
              <ArrowLeft className="w-4 h-4" />
              {t.backToBlog}
            </button>

            {selectedPost.featured_image && (
              <div className="aspect-video rounded-2xl overflow-hidden mb-10">
                <img 
                  src={selectedPost.featured_image} 
                  alt={language === 'en' ? selectedPost.title : (selectedPost.title_es || selectedPost.title)}
                  className="w-full h-full object-cover"
                />
              </div>
            )}

            <div className="flex flex-wrap items-center gap-4 text-sm text-stone-500 mb-6">
              <span className="bg-champagne px-4 py-2 rounded-full text-terracota font-semibold">
                {selectedPost.tag}
              </span>
              <span className="flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                {formatDate(selectedPost.created_at)}
              </span>
              <span className="flex items-center gap-2">
                <Clock className="w-4 h-4" />
                5 {t.readTime}
              </span>
            </div>

            <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl font-bold text-stone-900 leading-tight mb-8">
              {language === 'en' ? selectedPost.title : (selectedPost.title_es || selectedPost.title)}
            </h1>

            <p className="text-xl md:text-2xl text-stone-600 mb-10 border-l-4 border-terracota pl-6 italic">
              {language === 'en' ? selectedPost.excerpt : (selectedPost.excerpt_es || selectedPost.excerpt)}
            </p>

            <div className="prose prose-lg max-w-none prose-headings:font-serif prose-headings:text-stone-900 prose-p:text-stone-600 prose-a:text-terracota prose-strong:text-stone-900">
              {(language === 'en' ? selectedPost.content : (selectedPost.content_es || selectedPost.content))?.split('\n').map((paragraph, i) => {
                if (paragraph.startsWith('# ')) {
                  return <h1 key={i} className="text-4xl mt-10 mb-6 font-bold">{paragraph.replace('# ', '')}</h1>;
                } else if (paragraph.startsWith('## ')) {
                  return <h2 key={i} className="text-3xl mt-8 mb-4 font-bold">{paragraph.replace('## ', '')}</h2>;
                } else if (paragraph.startsWith('### ')) {
                  return <h3 key={i} className="text-2xl mt-6 mb-3 font-bold">{paragraph.replace('### ', '')}</h3>;
                } else if (paragraph.startsWith('---')) {
                  return <hr key={i} className="my-10 border-stone-200" />;
                } else if (paragraph.trim()) {
                  return <p key={i} className="mb-5 leading-relaxed text-lg">{paragraph}</p>;
                }
                return null;
              })}
            </div>

            <div className="mt-16 pt-8 border-t border-stone-200 flex items-center justify-between">
              <div>
                <p className="text-stone-500 text-sm">
                  {language === 'es' ? 'Escrito por' : 'Written by'}
                </p>
                <p className="text-stone-900 font-semibold text-lg">{selectedPost.author}</p>
              </div>
              <Link to="/contact">
                <button className="bg-terracota text-white px-6 py-3 rounded-full font-semibold hover:bg-stone-900 transition-colors flex items-center gap-2">
                  {language === 'es' ? 'Trabajemos Juntos' : 'Work Together'}
                  <ArrowRight className="w-4 h-4" />
                </button>
              </Link>
            </div>
          </div>
        </article>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-vainilla" data-testid="blog-page">
      {/* Hero Section - Editorial */}
      <section className="pt-32 pb-20 px-6 bg-stone-900 text-white">
        <div className="max-w-5xl mx-auto text-center">
          <p className="text-terracota text-sm font-bold tracking-[0.3em] uppercase mb-6">
            {t.overline}
          </p>
          <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl font-bold leading-[0.95] mb-6">
            {t.title}
          </h1>
          <p className="text-xl md:text-2xl text-stone-400 max-w-3xl mx-auto leading-relaxed">
            {t.subtitle}
          </p>
        </div>
      </section>

      {/* Scrolling Marquee */}
      <TextMarquee 
        lines={t.marqueeText}
        speed={25}
        dark={false}
      />

      {/* Posts Grid */}
      <section className="py-24 px-6">
        <div className="max-w-7xl mx-auto">
          {posts.length === 0 ? (
            <div className="text-center py-20">
              <p className="text-stone-500 text-xl">{t.noPosts}</p>
            </div>
          ) : (
            <>
              {/* Featured Post */}
              {posts.length > 0 && (
                <div 
                  className="mb-20 cursor-pointer group"
                  onClick={() => setSelectedPost(posts[0])}
                  data-testid="featured-post"
                >
                  <div className="grid lg:grid-cols-2 gap-10 items-center">
                    <div className="aspect-[4/3] rounded-2xl overflow-hidden bg-champagne">
                      {posts[0].featured_image ? (
                        <img 
                          src={posts[0].featured_image} 
                          alt={language === 'en' ? posts[0].title : (posts[0].title_es || posts[0].title)}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-champagne to-stone-100">
                          <span className="font-serif text-8xl text-terracota/20">
                            {(language === 'en' ? posts[0].title : (posts[0].title_es || posts[0].title)).charAt(0)}
                          </span>
                        </div>
                      )}
                    </div>
                    <div>
                      <div className="flex items-center gap-4 mb-4">
                        <span className="bg-terracota text-white text-xs font-bold tracking-wider uppercase px-4 py-2 rounded-full">
                          {language === 'es' ? 'Destacado' : 'Featured'}
                        </span>
                        <span className="text-stone-500 text-sm">
                          {formatDate(posts[0].created_at)}
                        </span>
                      </div>
                      <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl font-bold text-stone-900 mb-4 group-hover:text-terracota transition-colors">
                        {language === 'en' ? posts[0].title : (posts[0].title_es || posts[0].title)}
                      </h2>
                      <p className="text-stone-600 text-lg leading-relaxed mb-6 line-clamp-3">
                        {language === 'en' ? posts[0].excerpt : (posts[0].excerpt_es || posts[0].excerpt)}
                      </p>
                      <span className="inline-flex items-center gap-2 text-terracota font-semibold group-hover:gap-3 transition-all">
                        {t.readMore}
                        <ArrowRight className="w-5 h-5" />
                      </span>
                    </div>
                  </div>
                </div>
              )}

              {/* Rest of Posts */}
              {posts.length > 1 && (
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                  {posts.slice(1).map((post) => (
                    <article 
                      key={post.id}
                      className="bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 group cursor-pointer"
                      onClick={() => setSelectedPost(post)}
                      data-testid={`post-${post.id}`}
                    >
                      {/* Image */}
                      <div className="aspect-video bg-champagne overflow-hidden">
                        {post.featured_image ? (
                          <img 
                            src={post.featured_image} 
                            alt={language === 'en' ? post.title : (post.title_es || post.title)}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-champagne to-stone-100">
                            <span className="font-serif text-5xl text-terracota/20">
                              {(language === 'en' ? post.title : (post.title_es || post.title)).charAt(0)}
                            </span>
                          </div>
                        )}
                      </div>

                      {/* Content */}
                      <div className="p-6">
                        <div className="flex items-center gap-3 mb-4">
                          <span className="text-xs bg-champagne text-terracota px-3 py-1 rounded-full font-semibold">
                            {post.tag}
                          </span>
                          <span className="text-xs text-stone-400">
                            {formatDate(post.created_at)}
                          </span>
                        </div>

                        <h3 className="font-serif text-xl md:text-2xl font-bold text-stone-900 mb-3 group-hover:text-terracota transition-colors line-clamp-2">
                          {language === 'en' ? post.title : (post.title_es || post.title)}
                        </h3>

                        <p className="text-stone-600 text-sm line-clamp-3 mb-5">
                          {language === 'en' ? post.excerpt : (post.excerpt_es || post.excerpt)}
                        </p>

                        <span className="inline-flex items-center text-terracota text-sm font-semibold group-hover:gap-2 transition-all">
                          {t.readMore}
                          <ArrowRight className="w-4 h-4 ml-1" />
                        </span>
                      </div>
                    </article>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </section>

      {/* Newsletter CTA */}
      <section className="py-24 bg-stone-900 text-white">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <h2 className="font-serif text-4xl md:text-5xl font-bold mb-6">
            {language === 'es' ? 'Mantente Inspirado' : 'Stay Inspired'}
          </h2>
          <p className="text-xl text-stone-400 mb-10">
            {language === 'es' 
              ? 'Perspectivas mensuales sobre experiencias, marca y operaciones.'
              : 'Monthly insights on experiences, brand, and operations.'}
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center max-w-md mx-auto">
            <input 
              type="email" 
              placeholder={language === 'es' ? 'Ingresa tu correo' : 'Enter your email'}
              className="flex-1 px-5 py-4 bg-stone-800 border border-stone-700 text-white placeholder:text-stone-500 focus:outline-none focus:border-terracota transition-colors rounded-full"
            />
            <button className="bg-terracota text-white px-8 py-4 font-semibold hover:bg-white hover:text-stone-900 transition-all rounded-full">
              {language === 'es' ? 'Suscribirse' : 'Subscribe'}
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default NewBlog;
